import { useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Users, Utensils, X, Check } from 'lucide-react'
import { api } from '@/lib/api'
import MealSlotCard, { AddSlotButton } from './MealSlotCard'
import { MacroBar } from './MacroBar'
import { DAY_LABELS_FULL, formatDate, sumDayMacros } from '@/lib/utils'
import type { DayPlan, WeeklyPlan } from '@/types/models'

interface Props {
  day: DayPlan
  plan: WeeklyPlan
  isSelected: boolean
  onSelect: () => void
}

export default function DayColumn({ day, plan, isSelected, onSelect }: Props) {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()
  const [editingGroupSize, setEditingGroupSize] = useState(false)
  const [gsInput, setGsInput] = useState(String(day.group_size ?? plan.default_group_size))

  const resolvedGroupSize = day.group_size ?? plan.default_group_size

  const dayMacros = sumDayMacros(day.slots ?? [])
  const slotCount = (day.slots ?? []).filter(s => s.slot_type === 'meal').length
  const filledCount = (day.slots ?? []).filter(s => s.dish_name && !s.is_skipped).length

  const updateDay = useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const token = await getToken()
      return api.patch(`/plans/${plan.id}/days/${day.id}`, body, token!)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['plan', plan.id] }),
  })

  const mealSlots = (day.slots ?? []).filter(s => s.slot_type === 'meal')
  const extraSlots = (day.slots ?? []).filter(s => s.slot_type !== 'meal')
  const nextSlotIndex = (day.slots ?? []).length

  const isEatingOut = day.is_eating_out

  return (
    <div
      className={`flex flex-col min-w-[200px] rounded-2xl border-2 transition-all cursor-pointer ${
        isSelected
          ? 'border-brand-400 shadow-md'
          : 'border-gray-200 hover:border-gray-300'
      } ${isEatingOut ? 'opacity-60' : ''}`}
      onClick={onSelect}
    >
      {/* Day header */}
      <div className={`px-3 pt-3 pb-2 rounded-t-xl ${isSelected ? 'bg-brand-50' : 'bg-gray-50'}`}>
        <div className="flex items-center justify-between mb-0.5">
          <div>
            <p className={`text-sm font-bold ${isSelected ? 'text-brand-700' : 'text-gray-800'}`}>
              {DAY_LABELS_FULL[day.day_index]}
            </p>
            <p className="text-xs text-gray-400">{formatDate(day.date)}</p>
          </div>
          {/* Filled indicator */}
          <div className={`text-xs font-medium px-2 py-0.5 rounded-full ${
            filledCount === slotCount && slotCount > 0
              ? 'bg-green-100 text-green-700'
              : 'bg-gray-100 text-gray-500'
          }`}>
            {filledCount}/{slotCount}
          </div>
        </div>

        {/* Group size + eating out controls */}
        <div className="flex items-center gap-2 mt-1.5" onClick={e => e.stopPropagation()}>
          {editingGroupSize ? (
            <div className="flex items-center gap-1">
              <input
                type="number" min={1} max={20} value={gsInput}
                onChange={e => setGsInput(e.target.value)}
                className="w-12 text-xs border border-gray-300 rounded px-1.5 py-0.5 focus:ring-1 focus:ring-brand-500"
                autoFocus
              />
              <button onClick={() => {
                const gs = Math.min(20, Math.max(1, parseInt(gsInput)))
                updateDay.mutate({ group_size: gs })
                setEditingGroupSize(false)
              }} className="text-green-600 hover:text-green-700">
                <Check className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => setEditingGroupSize(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => { setGsInput(String(resolvedGroupSize)); setEditingGroupSize(true) }}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-brand-600"
              title="Override group size for this day"
            >
              <Users className="w-3 h-3" />
              {resolvedGroupSize}p
              {day.group_size && <span className="text-brand-400">*</span>}
            </button>
          )}

          <button
            onClick={() => updateDay.mutate({ is_eating_out: !isEatingOut })}
            className={`flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full border transition-colors ${
              isEatingOut
                ? 'border-orange-300 bg-orange-50 text-orange-600'
                : 'border-gray-200 text-gray-400 hover:text-orange-500'
            }`}
            title="Mark day as eating out"
          >
            <Utensils className="w-3 h-3" />
            {isEatingOut ? 'Out' : 'In'}
          </button>
        </div>
      </div>

      {/* Meal slots */}
      <div className="flex-1 p-2 space-y-2" onClick={e => e.stopPropagation()}>
        {isEatingOut ? (
          <div className="flex items-center justify-center h-20 text-xs text-gray-400 italic">
            Eating out — excluded from grocery list
          </div>
        ) : (
          <>
            {mealSlots.map(slot => (
              <MealSlotCard
                key={slot.id}
                slot={slot}
                planId={plan.id}
                resolvedGroupSize={resolvedGroupSize}
                calorie_target={plan.calorie_target}
              />
            ))}

            {/* Extra slots (snacks/beverages) */}
            {extraSlots.length > 0 && (
              <div className="pt-1 border-t border-dashed border-gray-200 space-y-1.5">
                {extraSlots.map(slot => (
                  <MealSlotCard
                    key={slot.id}
                    slot={slot}
                    planId={plan.id}
                    resolvedGroupSize={resolvedGroupSize}
                    calorie_target={null}
                  />
                ))}
              </div>
            )}

            <AddSlotButton
              dayId={day.id}
              planId={plan.id}
              nextIndex={nextSlotIndex}
              groupSize={resolvedGroupSize}
            />
          </>
        )}
      </div>

      {/* Day macro tally */}
      {!isEatingOut && (
        <div className="px-3 pb-3 pt-2 border-t border-gray-100 space-y-1.5">
          {plan.goal_mode === 'calories' ? (
            <MacroBar
              label="Calories"
              value={dayMacros.calories ?? 0}
              target={(plan.calorie_target ?? 0) * resolvedGroupSize}
              unit="kcal"
              color="bg-orange-400"
            />
          ) : (
            <>
              <MacroBar label="Protein" value={dayMacros.protein_g ?? 0} target={(plan.protein_target_g ?? 0) * resolvedGroupSize} color="bg-blue-400" />
              <MacroBar label="Carbs" value={dayMacros.carbs_g ?? 0} target={(plan.carbs_target_g ?? 0) * resolvedGroupSize} color="bg-yellow-400" />
              <MacroBar label="Fat" value={dayMacros.fat_g ?? 0} target={(plan.fat_target_g ?? 0) * resolvedGroupSize} color="bg-purple-400" />
            </>
          )}
        </div>
      )}
    </div>
  )
}
